import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import toast from 'react-hot-toast';

import { useAuthStore } from '@/store/auth.store';

const baseURL = import.meta.env.VITE_API_URL?.trim() || '';

export const api: AxiosInstance = axios.create({ baseURL, timeout: 20_000 });

api.interceptors.request.use((cfg) => {
  const t = useAuthStore.getState().accessToken;
  if (t) {
    cfg.headers = cfg.headers ?? {};
    cfg.headers['Authorization'] = `Bearer ${t}`;
  }
  return cfg;
});

// Защита от параллельных refresh-запросов: все 401-ответы ждут один общий refresh.
// Если refresh упадёт — logout + ошибка; если ок — повторяем оригинальные запросы.
let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const { refreshToken, setSession, user, logout } = useAuthStore.getState();
  if (!refreshToken) {
    logout();
    return null;
  }
  try {
    const { data } = await axios.post<{
      access_token: string;
      refresh_token: string;
      user?: {
        id: string;
        name?: string | null;
        phone?: string | null;
        email?: string | null;
      };
    }>(
      `${baseURL}/api/auth/refresh`,
      { refresh_token: refreshToken },
      { timeout: 20_000 },
    );
    setSession({
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      // Backend возвращает обновлённые поля user; если его не дал — оставляем прежнего.
      user: data.user
        ? {
            id: data.user.id,
            name: data.user.name ?? null,
            phone: data.user.phone ?? null,
            email: data.user.email ?? null,
          }
        : user ?? { id: '' },
    });
    return data.access_token;
  } catch {
    logout();
    return null;
  }
}

// URLs, на которые НЕ пытаемся рефрешить токен: это сами auth-эндпоинты,
// ошибки от них — это валидная бизнес-ошибка (неверный код и т.п.).
function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return (
    url.includes('/api/auth/refresh') ||
    url.includes('/api/auth/client/send-code') ||
    url.includes('/api/auth/client/verify-code') ||
    url.includes('/api/auth/client/email/send-code') ||
    url.includes('/api/auth/client/email/verify-code')
  );
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError<{ error?: string }>) => {
    const status = err.response?.status;
    const original = err.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;

    if (status === 401 && original && !original._retry && !isAuthEndpoint(original.url)) {
      original._retry = true;
      refreshInFlight = refreshInFlight ?? refreshAccessToken();
      const newToken = await refreshInFlight;
      refreshInFlight = null;
      if (newToken) {
        original.headers = original.headers ?? {};
        original.headers['Authorization'] = `Bearer ${newToken}`;
        return api.request(original as AxiosRequestConfig);
      }
      // refresh не получилось — сбрасываем сессию и показываем человеческую ошибку.
      toast.error('Сессия истекла. Войдите снова.');
      return Promise.reject(err);
    }

    const msg = err.response?.data?.error ?? err.message ?? 'Ошибка запроса';
    if (status && status >= 400) {
      toast.error(msg);
    }
    return Promise.reject(err);
  },
);

export async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const { data } = await api.get<T>(url, { params });
  return data;
}
export async function post<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.post<T>(url, body);
  return data;
}
export async function patch<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.patch<T>(url, body);
  return data;
}
export async function del<T>(url: string): Promise<T> {
  const { data } = await api.delete<T>(url);
  return data;
}
